"use client";

import { useQuery } from "@tanstack/react-query";
import { Cable } from "lucide-react";

import { getRuntimeNetwork } from "@/lib/api/endpoints/runtime";
import { cn } from "@/lib/utils/cn";

import {
  wizardFieldClass,
  wizardIconBoxClass,
  wizardInvalidInputClass,
  wizardKickerClass,
  wizardLabelClass,
  wizardSectionShellClass,
  wizardTextareaClass,
} from "../form-primitives";
import { ProtocolSelect } from "../protocol-select";
import { useDataloggerWizardStore } from "../../hooks/use-datalogger-form";
import {
  CONNECTIVITY_PROTOCOL_OPTIONS,
  type HardwareProtocolSettings,
} from "../../schema/datalogger.schema";

const checkboxClass = "h-4 w-4 rounded border-border text-primary focus:ring-primary/20";

export function StepBasicConnectivity({
  invalidFieldKey = null,
  validationErrors = [],
  onFieldEdit,
}: {
  invalidFieldKey?: string | null;
  validationErrors?: string[];
  onFieldEdit?: () => void;
}) {
  const mode = useDataloggerWizardStore((s) => s.hardwareDraft.transmissionMode);
  const ps = useDataloggerWizardStore((s) => s.hardwareDraft.protocolSettings);
  const setTransmissionMode = useDataloggerWizardStore((s) => s.setTransmissionMode);
  const setProtocolSettings = useDataloggerWizardStore((s) => s.setProtocolSettings);
  const runtimeNetworkQuery = useQuery({
    queryKey: ["runtime-network"],
    queryFn: getRuntimeNetwork,
    staleTime: 60_000,
  });
  const telemetrySyncEndpoint = runtimeNetworkQuery.data?.telemetry_sync_url ?? "/telemetry/sync";
  const lanLabel = runtimeNetworkQuery.data?.preferred_ipv4 ? `Detected LAN IP: ${runtimeNetworkQuery.data.preferred_ipv4}` : null;

  const selectedMeta = CONNECTIVITY_PROTOCOL_OPTIONS.find((o) => o.value === mode);
  const fieldError = validationErrors[0] ?? null;

  const updateProtocolSettings = (patch: Partial<HardwareProtocolSettings>) => {
    onFieldEdit?.();
    setProtocolSettings(patch);
  };

  return (
    <section className={cn(wizardSectionShellClass)}>
      <div className="mb-ds-5 flex items-center gap-ds-3">
        <div className={wizardIconBoxClass}>
          <Cable className="h-4 w-4" aria-hidden />
        </div>
        <div>
          <p className={wizardKickerClass}>Connectivity & protocol</p>
          <p className="text-caption text-muted">
            How this logger reaches the platform (commissioning hints - devices may override locally)
          </p>
        </div>
      </div>

      <div className="flex flex-col gap-ds-4">
        <div className="flex flex-col gap-ds-2">
          <label htmlFor="datalogger-protocol" className={wizardLabelClass}>
            Protocol / uplink
          </label>
          <ProtocolSelect
            id="datalogger-protocol"
            value={mode}
            onChange={(next) => {
              onFieldEdit?.();
              setTransmissionMode(next);
            }}
          />
          {selectedMeta ? <p className="text-[0.7rem] leading-relaxed text-muted">{selectedMeta.hint}</p> : null}
        </div>

        {mode === "mqtt" ? (
          <div className="grid gap-ds-3 sm:grid-cols-2">
            <div className="flex flex-col gap-ds-2 sm:col-span-2">
              <label className={wizardLabelClass}>Broker host</label>
              <input
                className={wizardFieldClass}
                value={ps.mqttBroker ?? ""}
                onChange={(e) => updateProtocolSettings({ mqttBroker: e.target.value })}
                placeholder="mqtt.example.com"
                autoComplete="off"
              />
            </div>
            <div className="flex flex-col gap-ds-2">
              <label className={wizardLabelClass}>Port</label>
              <input
                className={cn(wizardFieldClass, "tabular-nums", invalidFieldKey === "protocol.mqttPort" && wizardInvalidInputClass)}
                data-validation-key="protocol.mqttPort"
                aria-invalid={invalidFieldKey === "protocol.mqttPort" || undefined}
                aria-describedby={invalidFieldKey === "protocol.mqttPort" ? "protocol-mqtt-port-error" : undefined}
                value={ps.mqttPort ?? ""}
                onChange={(e) => updateProtocolSettings({ mqttPort: e.target.value })}
                placeholder="8883"
                inputMode="numeric"
              />
              {invalidFieldKey === "protocol.mqttPort" && fieldError ? (
                <span id="protocol-mqtt-port-error" className="text-caption text-error">
                  {fieldError}
                </span>
              ) : null}
            </div>
            <div className="flex flex-col gap-ds-2">
              <label className={wizardLabelClass}>Topic prefix (optional)</label>
              <input
                className={wizardFieldClass}
                value={ps.mqttTopicPrefix ?? ""}
                onChange={(e) => updateProtocolSettings({ mqttTopicPrefix: e.target.value })}
                placeholder="workspace/site/logger"
              />
            </div>
            <div className="flex flex-col gap-ds-2 sm:col-span-2">
              <label className={wizardLabelClass}>Telemetry topic (optional override)</label>
              <input
                className={wizardFieldClass}
                value={ps.mqttTopic ?? ""}
                onChange={(e) => updateProtocolSettings({ mqttTopic: e.target.value })}
                placeholder="devices/{device_uid}/telemetry"
                autoComplete="off"
              />
              <p className="text-[0.7rem] leading-relaxed text-muted">
                Leave blank to auto-build from prefix, or use <code>{`{device_uid}`}</code> as a placeholder.
              </p>
            </div>
            <div className="flex flex-col gap-ds-2">
              <label className={wizardLabelClass}>MQTT username (optional)</label>
              <input
                className={wizardFieldClass}
                value={ps.mqttUsername ?? ""}
                onChange={(e) => updateProtocolSettings({ mqttUsername: e.target.value })}
                placeholder="broker username"
                autoComplete="off"
              />
            </div>
            <div className="flex flex-col gap-ds-2">
              <label className={wizardLabelClass}>MQTT password (optional)</label>
              <input
                className={wizardFieldClass}
                type="password"
                value={ps.mqttPassword ?? ""}
                onChange={(e) => updateProtocolSettings({ mqttPassword: e.target.value })}
                placeholder="broker password"
                autoComplete="new-password"
              />
            </div>
            <div className="flex flex-col gap-ds-2 sm:col-span-2">
              <label className={wizardLabelClass}>Publisher client ID (optional)</label>
              <input
                className={wizardFieldClass}
                value={ps.mqttPublisherClientId ?? ""}
                onChange={(e) => updateProtocolSettings({ mqttPublisherClientId: e.target.value })}
                placeholder="4791000000199001"
                autoComplete="off"
              />
            </div>
            <label className="flex cursor-pointer items-center gap-ds-2 sm:col-span-2">
              <input
                type="checkbox"
                className={checkboxClass}
                checked={!!ps.mqttTls}
                onChange={(e) => updateProtocolSettings({ mqttTls: e.target.checked })}
              />
              <span className="text-caption text-foreground">Use TLS</span>
            </label>
          </div>
        ) : null}

        {mode === "http" ? (
          <div className="flex flex-col gap-ds-2">
            <label className={wizardLabelClass}>Telemetry endpoint</label>
            <div
              className={cn(
                wizardFieldClass,
                "flex min-h-11 items-center bg-muted-bg/30 text-foreground/85",
              )}
              aria-readonly="true"
            >
              <span className="break-all text-[0.8rem]">{telemetrySyncEndpoint}</span>
            </div>
            <p className="text-[0.7rem] leading-relaxed text-muted">
              HTTP/REST devices should POST telemetry to this endpoint. This value is fixed and cannot be edited here.
            </p>
          </div>
        ) : null}

        {mode === "ethernet" || mode === "wifi" ? (
          <div className="space-y-ds-4 rounded-[var(--radius-lg)] border border-border/50 bg-muted-bg/15 p-ds-4">
            <div className="flex flex-col gap-ds-2">
              <label className={wizardLabelClass}>Telemetry endpoint</label>
              <div className={cn(wizardFieldClass, "flex min-h-11 items-center bg-muted-bg/30 text-foreground/85")}>
                <span className="break-all text-[0.8rem]">{telemetrySyncEndpoint}</span>
              </div>
            </div>

            <div className="space-y-1">
              {lanLabel ? <p className="text-[0.7rem] leading-relaxed text-muted">{lanLabel}</p> : null}
              <p className="text-[0.7rem] leading-relaxed text-muted">
                Local LAN modes use the same backend ingest pipeline as HTTP/REST. Devices on the same network should post telemetry to this endpoint using the generated device credentials.
              </p>
            </div>
          </div>
        ) : null}

        {mode === "websocket" ? (
          <div className="flex flex-col gap-ds-2">
            <label className={wizardLabelClass}>WebSocket URL</label>
            <input
              className={wizardFieldClass}
              value={ps.websocketUrl ?? ""}
              onChange={(e) => updateProtocolSettings({ websocketUrl: e.target.value })}
              placeholder="wss://edge.example.com/stream"
              autoComplete="off"
            />
          </div>
        ) : null}

        {mode === "polling" ? (
          <div className="flex flex-col gap-ds-2">
            <label className={wizardLabelClass}>Poll interval (seconds)</label>
            <input
              className={cn(
                wizardFieldClass,
                "tabular-nums",
                invalidFieldKey === "protocol.pollIntervalSeconds" && wizardInvalidInputClass,
              )}
              data-validation-key="protocol.pollIntervalSeconds"
              aria-invalid={invalidFieldKey === "protocol.pollIntervalSeconds" || undefined}
              aria-describedby={
                invalidFieldKey === "protocol.pollIntervalSeconds" ? "protocol-poll-interval-error" : undefined
              }
              value={ps.pollIntervalSeconds ?? ""}
              onChange={(e) => updateProtocolSettings({ pollIntervalSeconds: e.target.value })}
              placeholder="60"
              inputMode="numeric"
            />
            {invalidFieldKey === "protocol.pollIntervalSeconds" && fieldError ? (
              <span id="protocol-poll-interval-error" className="text-caption text-error">
                {fieldError}
              </span>
            ) : null}
          </div>
        ) : null}

        {mode === "bluetooth_serial" ? (
          <div className="grid gap-ds-3 sm:grid-cols-2">
            <div className="flex flex-col gap-ds-2">
              <label className={wizardLabelClass}>Device name</label>
              <input
                className={wizardFieldClass}
                value={ps.bluetoothDeviceName ?? ""}
                onChange={(e) => updateProtocolSettings({ bluetoothDeviceName: e.target.value })}
                placeholder="ESP32_SHT40"
                autoComplete="off"
              />
            </div>
            <div className="flex flex-col gap-ds-2">
              <label className={wizardLabelClass}>Windows COM port</label>
              <input
                className={cn(wizardFieldClass, invalidFieldKey === "protocol.bluetoothPort" && wizardInvalidInputClass)}
                data-validation-key="protocol.bluetoothPort"
                aria-invalid={invalidFieldKey === "protocol.bluetoothPort" || undefined}
                aria-describedby={invalidFieldKey === "protocol.bluetoothPort" ? "protocol-bluetooth-port-error" : undefined}
                value={ps.bluetoothPort ?? ""}
                onChange={(e) => updateProtocolSettings({ bluetoothPort: e.target.value })}
                placeholder="COM5"
                autoComplete="off"
              />
              {invalidFieldKey === "protocol.bluetoothPort" && fieldError ? (
                <span id="protocol-bluetooth-port-error" className="text-caption text-error">
                  {fieldError}
                </span>
              ) : null}
            </div>
            <div className="flex flex-col gap-ds-2">
              <label className={wizardLabelClass}>Baud rate</label>
              <input
                className={cn(
                  wizardFieldClass,
                  "tabular-nums",
                  invalidFieldKey === "protocol.bluetoothBaudRate" && wizardInvalidInputClass,
                )}
                data-validation-key="protocol.bluetoothBaudRate"
                aria-invalid={invalidFieldKey === "protocol.bluetoothBaudRate" || undefined}
                aria-describedby={
                  invalidFieldKey === "protocol.bluetoothBaudRate" ? "protocol-bluetooth-baud-error" : undefined
                }
                value={ps.bluetoothBaudRate ?? "115200"}
                onChange={(e) => updateProtocolSettings({ bluetoothBaudRate: e.target.value })}
                placeholder="115200"
                inputMode="numeric"
              />
              {invalidFieldKey === "protocol.bluetoothBaudRate" && fieldError ? (
                <span id="protocol-bluetooth-baud-error" className="text-caption text-error">
                  {fieldError}
                </span>
              ) : null}
            </div>
            <div className="flex flex-col gap-ds-2">
              <label className={wizardLabelClass}>Line ending</label>
              <input
                className={wizardFieldClass}
                value={ps.bluetoothLineEnding ?? "\\n"}
                onChange={(e) => updateProtocolSettings({ bluetoothLineEnding: e.target.value })}
                placeholder="\\n"
                autoComplete="off"
              />
            </div>
            <div className="flex flex-col gap-ds-2 sm:col-span-2">
              <label className={wizardLabelClass}>Raw reading format</label>
              <input
                className={wizardFieldClass}
                value={ps.bluetoothDataFormat ?? "TEMP:{temp},HUM:{hum}"}
                onChange={(e) => updateProtocolSettings({ bluetoothDataFormat: e.target.value })}
                placeholder="TEMP:{temp},HUM:{hum}"
                autoComplete="off"
              />
              <p className="text-[0.7rem] leading-relaxed text-muted">
                Classic Bluetooth SPP is read from the Windows COM port after pairing. Channel labels are trimmed and lowercased, so TEMP becomes temp and HUM becomes hum.
              </p>
            </div>
            <div className="flex flex-col gap-ds-2 sm:col-span-2">
              <label className={wizardLabelClass}>Pairing / installer notes</label>
              <textarea
                className={wizardTextareaClass}
                value={ps.bluetoothNote ?? ""}
                onChange={(e) => updateProtocolSettings({ bluetoothNote: e.target.value })}
                placeholder="Direct pairing, adapter location, or field notes..."
              />
            </div>
          </div>
        ) : null}

        {mode === "cellular" ? (
          <div className="flex flex-col gap-ds-2">
            <label className={wizardLabelClass}>APN (optional)</label>
            <input
              className={wizardFieldClass}
              value={ps.cellularApn ?? ""}
              onChange={(e) => updateProtocolSettings({ cellularApn: e.target.value })}
              placeholder="carrier.apn.name"
            />
          </div>
        ) : null}

        {mode === "modbus_tcp" ? (
          <div className="grid gap-ds-3 sm:grid-cols-3">
            <div className="flex flex-col gap-ds-2 sm:col-span-2">
              <label className={wizardLabelClass}>Host / IP</label>
              <input
                className={cn(wizardFieldClass, invalidFieldKey === "protocol.modbusHost" && wizardInvalidInputClass)}
                data-validation-key="protocol.modbusHost"
                aria-invalid={invalidFieldKey === "protocol.modbusHost" || undefined}
                aria-describedby={invalidFieldKey === "protocol.modbusHost" ? "protocol-modbus-host-error" : undefined}
                value={ps.modbusHost ?? ""}
                onChange={(e) => updateProtocolSettings({ modbusHost: e.target.value })}
              />
              {invalidFieldKey === "protocol.modbusHost" && fieldError ? (
                <span id="protocol-modbus-host-error" className="text-caption text-error">
                  {fieldError}
                </span>
              ) : null}
            </div>
            <div className="flex flex-col gap-ds-2">
              <label className={wizardLabelClass}>Port</label>
              <input
                className={cn(
                  wizardFieldClass,
                  "tabular-nums",
                  invalidFieldKey === "protocol.modbusPort" && wizardInvalidInputClass,
                )}
                data-validation-key="protocol.modbusPort"
                aria-invalid={invalidFieldKey === "protocol.modbusPort" || undefined}
                aria-describedby={invalidFieldKey === "protocol.modbusPort" ? "protocol-modbus-port-error" : undefined}
                value={ps.modbusPort ?? ""}
                onChange={(e) => updateProtocolSettings({ modbusPort: e.target.value })}
                placeholder="502"
              />
              {invalidFieldKey === "protocol.modbusPort" && fieldError ? (
                <span id="protocol-modbus-port-error" className="text-caption text-error">
                  {fieldError}
                </span>
              ) : null}
            </div>
            <div className="flex flex-col gap-ds-2">
              <label className={wizardLabelClass}>Unit ID (optional)</label>
              <input
                className={cn(wizardFieldClass, "tabular-nums")}
                value={ps.modbusUnitId ?? ""}
                onChange={(e) => updateProtocolSettings({ modbusUnitId: e.target.value })}
              />
            </div>
          </div>
        ) : null}

        {mode === "lora" ? (
          <div className="grid gap-ds-3 sm:grid-cols-2">
            <div className="flex flex-col gap-ds-2">
              <label className={wizardLabelClass}>Region (optional)</label>
              <input
                className={wizardFieldClass}
                value={ps.loraRegion ?? ""}
                onChange={(e) => updateProtocolSettings({ loraRegion: e.target.value })}
                placeholder="IN865 / EU868 / US915"
                autoComplete="off"
              />
            </div>
            <div className="flex flex-col gap-ds-2">
              <label className={wizardLabelClass}>Network server (optional)</label>
              <input
                className={wizardFieldClass}
                value={ps.loraNetworkServer ?? ""}
                onChange={(e) => updateProtocolSettings({ loraNetworkServer: e.target.value })}
                placeholder="The Things Stack / ChirpStack"
                autoComplete="off"
              />
            </div>
            <div className="flex flex-col gap-ds-2">
              <label className={wizardLabelClass}>AppEUI / JoinEUI (optional)</label>
              <input
                className={cn(wizardFieldClass, invalidFieldKey === "protocol.loraAppEui" && wizardInvalidInputClass)}
                data-validation-key="protocol.loraAppEui"
                aria-invalid={invalidFieldKey === "protocol.loraAppEui" || undefined}
                aria-describedby={invalidFieldKey === "protocol.loraAppEui" ? "protocol-lora-app-eui-error" : undefined}
                value={ps.loraAppEui ?? ""}
                onChange={(e) => updateProtocolSettings({ loraAppEui: e.target.value })}
                placeholder="70B3D57ED005ABCD"
                autoComplete="off"
              />
              {invalidFieldKey === "protocol.loraAppEui" && fieldError ? (
                <span id="protocol-lora-app-eui-error" className="text-caption text-error">
                  {fieldError}
                </span>
              ) : null}
            </div>
            <div className="flex flex-col gap-ds-2">
              <label className={wizardLabelClass}>DevEUI</label>
              <input
                className={cn(wizardFieldClass, invalidFieldKey === "protocol.loraDevEui" && wizardInvalidInputClass)}
                data-validation-key="protocol.loraDevEui"
                aria-invalid={invalidFieldKey === "protocol.loraDevEui" || undefined}
                aria-describedby={invalidFieldKey === "protocol.loraDevEui" ? "protocol-lora-dev-eui-error" : undefined}
                value={ps.loraDevEui ?? ""}
                onChange={(e) => updateProtocolSettings({ loraDevEui: e.target.value })}
                placeholder="A840411B828A1234"
                autoComplete="off"
              />
              {invalidFieldKey === "protocol.loraDevEui" && fieldError ? (
                <span id="protocol-lora-dev-eui-error" className="text-caption text-error">
                  {fieldError}
                </span>
              ) : null}
            </div>
            <div className="flex flex-col gap-ds-2 sm:col-span-2">
              <label className={wizardLabelClass}>AppKey (optional for now)</label>
              <input
                className={cn(wizardFieldClass, invalidFieldKey === "protocol.loraAppKey" && wizardInvalidInputClass)}
                data-validation-key="protocol.loraAppKey"
                aria-invalid={invalidFieldKey === "protocol.loraAppKey" || undefined}
                aria-describedby={invalidFieldKey === "protocol.loraAppKey" ? "protocol-lora-app-key-error" : undefined}
                type="password"
                value={ps.loraAppKey ?? ""}
                onChange={(e) => updateProtocolSettings({ loraAppKey: e.target.value })}
                placeholder="0123456789ABCDEF0123456789ABCDEF"
                autoComplete="new-password"
              />
              {invalidFieldKey === "protocol.loraAppKey" && fieldError ? (
                <span id="protocol-lora-app-key-error" className="text-caption text-error">
                  {fieldError}
                </span>
              ) : null}
            </div>
          </div>
        ) : null}
      </div>
    </section>
  );
}
